// frontend/src/features/aw/AWClientManager.jsx
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { getClients, createClient, deleteClient, addContact, deleteContact, importClients } from "./api";

/** Render children into document.body so position:fixed works regardless of ancestor transforms/filters */
function Portal({ children }) {
  return ReactDOM.createPortal(
    <div style={{
      "--bg":"#1b191e","--surface":"rgba(40,36,42,.55)","--surface2":"#2e2a32",
      "--border":"#3d3841","--border-l":"rgba(60,55,64,.45)",
      "--text":"#ece8ed","--muted":"#a0979a","--dim":"#726a72",
      "--accent":"#8B2332","--accent-g":"rgba(139,35,50,.12)",
      "--ease":"cubic-bezier(0.16, 1, 0.3, 1)",
      fontFamily:"'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,sans-serif",
      color:"var(--text)",
    }}>{children}</div>,
    document.body
  );
}

function fixName(name) {
  if (!name) return "";
  const s = name.trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) return `${m[2].trim()} ${m[1].trim()}`;
  return s;
}

export default function AWClientManager() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [addingContactFor, setAddingContactFor] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => { try { setClients(await getClients()); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const handleDeleteClient = async (id) => {
    if (!window.confirm("Delete this client and all contacts?")) return;
    await deleteClient(id); await load();
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await deleteContact(id); await load();
  };

  const filtered = search
    ? clients.filter(c => [c.name, c.top_level_company, c.account_handler, ...(c.contacts || []).map(ct => ct.full_name)]
        .some(v => v && v.toLowerCase().includes(search.toLowerCase())))
    : clients;

  const totalContacts = clients.reduce((n, c) => n + (c.contacts?.length || 0), 0);

  return (
    <div className="aw-cm">
      <style>{CM_CSS}</style>

      {/* Header */}
      <div className="aw-cm-header">
        <div className="aw-cm-header-left">
          <div className="aw-cm-icon-wrap">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </div>
          <div>
            <h2 className="aw-cm-title">Clients & Contacts</h2>
            <p className="aw-cm-sub">{clients.length} clients &middot; {totalContacts} contacts</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="aw-btn" onClick={() => setShowImport(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
            Import
          </button>
          <button className="aw-btn-primary" onClick={() => setShowNewClient(true)}>+ New Client</button>
        </div>
      </div>

      {/* Search */}
      <div className="aw-cm-search-wrap">
        <svg className="aw-cm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input className="aw-cm-search" placeholder="Search clients or contacts..." value={search} onChange={e => setSearch(e.target.value)} />
        {search && <button className="aw-cm-search-clear" onClick={() => setSearch("")}>&times;</button>}
      </div>

      {/* Client list */}
      {loading ? (
        <div className="aw-cm-empty">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" style={{ animation: "aw-spin 1s linear infinite" }}>
            <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4m-3.93 7.07l-2.83-2.83M7.76 7.76L4.93 4.93"/>
          </svg>
          <div style={{ fontSize: 13, color: "var(--dim)", marginTop: 8 }}>Loading clients...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="aw-cm-empty">
          {clients.length === 0 ? (
            <>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="1.2"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8 }}>No clients yet</div>
              <div style={{ fontSize: 12, color: "var(--dim)" }}>Import from Excel or add one manually</div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: "var(--dim)" }}>No clients match "{search}"</div>
          )}
        </div>
      ) : (
        <div className="aw-cm-list">
          {filtered.map((c, ci) => {
            const isOpen = expanded === c.id;
            const contactCount = c.contacts?.length || 0;
            return (
              <div key={c.id} className={`aw-cm-card ${isOpen ? "aw-cm-card-open" : ""}`}
                style={{ animationDelay: ci < 10 ? `${ci * 25}ms` : "0ms" }}>
                {/* Client row */}
                <div className="aw-cm-card-row" onClick={() => setExpanded(isOpen ? null : c.id)}>
                  <div className="aw-cm-avatar">{(c.name || "?").charAt(0).toUpperCase()}</div>
                  <div className="aw-cm-card-info">
                    <div className="aw-cm-card-name">{c.name}</div>
                    <div className="aw-cm-card-meta">
                      {c.top_level_company && c.top_level_company !== c.name && (
                        <span className="aw-cm-tag">{c.top_level_company}</span>
                      )}
                      {c.account_handler && <span className="aw-cm-tag">{c.account_handler}</span>}
                      {c.city && c.state && <span>{c.city}, {c.state}</span>}
                    </div>
                  </div>
                  <div className={`aw-cm-contact-count ${contactCount > 0 ? "aw-cm-cc-has" : ""}`}>
                    {contactCount}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  </div>
                  <svg className="aw-cm-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--dim)" strokeWidth="2"
                    style={{ transform: isOpen ? "rotate(180deg)" : "none" }}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                  <button className="aw-act aw-act-red aw-cm-del" onClick={e => { e.stopPropagation(); handleDeleteClient(c.id); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </button>
                </div>

                {/* Contacts panel */}
                {isOpen && (
                  <div className="aw-cm-contacts">
                    {c.contacts?.length > 0 ? c.contacts.map(ct => (
                      <div key={ct.id} className="aw-cm-contact">
                        <div className="aw-cm-contact-avatar">{(ct.first_name || fixName(ct.full_name) || "?").charAt(0).toUpperCase()}</div>
                        <div className="aw-cm-contact-info">
                          <div className="aw-cm-contact-name">{fixName(ct.full_name)}</div>
                          <div className="aw-cm-contact-detail">
                            {ct.title && <span className="aw-cm-tag">{ct.title}</span>}
                            {ct.roles && <span className="aw-cm-tag aw-cm-tag-role">{ct.roles}</span>}
                          </div>
                        </div>
                        <div className="aw-cm-contact-right">
                          {ct.email && <div className="aw-cm-contact-line">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                            {ct.email}
                          </div>}
                          {ct.phone && <div className="aw-cm-contact-line">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                            {ct.phone}
                          </div>}
                          {ct.mobile && <div className="aw-cm-contact-line">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                            {ct.mobile}
                          </div>}
                        </div>
                        <button className="aw-act aw-act-red" style={{ flexShrink: 0 }} onClick={() => handleDeleteContact(ct.id)}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    )) : (
                      <div className="aw-cm-no-contacts">No contacts yet</div>
                    )}
                    <button className="aw-btn" style={{ fontSize: 11, marginTop: 6 }}
                      onClick={() => setAddingContactFor(c.id)}>+ Add Contact</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showImport && <Portal><ClientImportModal onDone={() => { setShowImport(false); load(); }} onClose={() => setShowImport(false)} /></Portal>}
      {showNewClient && <Portal><NewClientModal onDone={() => { setShowNewClient(false); load(); }} onClose={() => setShowNewClient(false)} /></Portal>}
      {addingContactFor && <Portal><NewContactModal clientId={addingContactFor}
        onDone={() => { setAddingContactFor(null); load(); }} onClose={() => setAddingContactFor(null)} /></Portal>}
    </div>
  );
}

// ── Import Modal ──
function ClientImportModal({ onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef();

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setError("");
    try { setResult(await importClients(file)); }
    catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
        <div className="aw-modal-hdr">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Import Clients & Contacts</h3>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          <div className="aw-cm-import-info">
            <div className="aw-import-info-label" style={{ fontSize: 10, fontWeight: 700, color: "var(--dim)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 6 }}>Expected columns</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {["Company Name", "Top Level Company Name", "Contact Full Name", "Contact Email", "Contact Phone", "Contact Title", "Contact Roles"].map(c => (
                <span key={c} style={{ padding: "2px 6px", borderRadius: 3, fontSize: 9, fontWeight: 600, background: "rgba(100,116,139,.1)", color: "var(--muted)", border: "1px solid var(--border-l)" }}>{c}</span>
              ))}
            </div>
          </div>
          <div style={{ border: "2px dashed var(--border)", borderRadius: 12, padding: "28px 16px", textAlign: "center", cursor: "pointer", background: "var(--surface)", marginTop: 12 }}
            onClick={() => inputRef.current?.click()}>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={e => setFile(e.target.files[0])} />
            {file ? <span style={{ fontWeight: 700, color: "var(--accent)" }}>{file.name}</span>
              : <span style={{ color: "var(--dim)", fontSize: 12 }}>Drop file or click to browse</span>}
          </div>
          {error && <div style={{ marginTop: 8, color: "#e8736a", fontSize: 12 }}>{error}</div>}
          {result && (
            <div style={{ marginTop: 12, padding: 14, borderRadius: 10, background: "rgba(0,0,0,.15)", border: "1px solid var(--border-l)", fontSize: 12 }}>
              <div style={{ display: "flex", gap: 20, justifyContent: "center", marginBottom: 4 }}>
                <span><strong style={{ color: "#50d87a" }}>{result.clients_imported}</strong> clients</span>
                <span><strong style={{ color: "#e8b040" }}>{result.clients_skipped}</strong> skipped</span>
                <span><strong style={{ color: "#7db5f0" }}>{result.contacts_imported}</strong> contacts</span>
              </div>
              {result.errors?.length > 0 && <div style={{ color: "#e8736a", fontSize: 10, marginTop: 4 }}>{result.errors.slice(0, 3).join("; ")}</div>}
            </div>
          )}
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          {result ? <button className="aw-btn-primary" onClick={onDone}>Done</button>
            : <button className="aw-btn-primary" onClick={handleUpload} disabled={!file || loading}>{loading ? "Importing..." : "Import"}</button>}
        </div>
      </div>
    </div>
  );
}

// ── New Client Modal ──
function NewClientModal({ onDone, onClose }) {
  const [form, setForm] = useState({ name: "", top_level_company: "", company_code: "", city: "", state: "", account_handler: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => { setLoading(true); try { await createClient(form); onDone(); } catch {} finally { setLoading(false); } };

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="aw-modal-hdr">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>New Client</h3>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          {[["name", "Company Name *"], ["top_level_company", "Top Level Company"], ["company_code", "Code"],
            ["city", "City"], ["state", "State"], ["account_handler", "Account Handler"]].map(([k, l]) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <label className="aw-label">{l}</label>
              <input className="aw-input" style={{ width: "100%" }} value={form[k]} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn-primary" onClick={save} disabled={!form.name || loading}>{loading ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ── New Contact Modal ──
function NewContactModal({ clientId, onDone, onClose }) {
  const [form, setForm] = useState({ full_name: "", first_name: "", last_name: "", title: "", email: "", phone: "", mobile: "", roles: "", address: "", city: "", state: "", zip: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => { setLoading(true); try { await addContact(clientId, form); onDone(); } catch {} finally { setLoading(false); } };

  return (
    <div className="aw-modal-bg" onClick={onClose}>
      <div className="aw-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="aw-modal-hdr">
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Add Contact</h3>
          <button className="aw-modal-x" onClick={onClose}>&times;</button>
        </div>
        <div className="aw-modal-body">
          {[["full_name", "Full Name *"], ["title", "Title"], ["email", "Email"],
            ["phone", "Phone"], ["mobile", "Mobile"], ["roles", "Roles"],
            ["address", "Address"], ["city", "City"], ["state", "State"], ["zip", "Zip"]].map(([k, l]) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <label className="aw-label">{l}</label>
              <input className="aw-input" style={{ width: "100%" }} value={form[k]} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
        </div>
        <div className="aw-modal-foot">
          <button className="aw-btn" onClick={onClose}>Cancel</button>
          <button className="aw-btn-primary" onClick={save} disabled={!form.full_name || loading}>{loading ? "Saving..." : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const CM_CSS = `
.aw-cm{max-width:800px;margin:0 auto;animation:aw-in .4s var(--ease) both;}

.aw-cm-header{display:flex;justify-content:space-between;align-items:center;margin-bottom:20px;}
.aw-cm-header-left{display:flex;align-items:center;gap:12px;}
.aw-cm-icon-wrap{
  width:44px;height:44px;border-radius:10px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.2);
}
.aw-cm-title{font-size:20px;font-weight:800;margin:0;line-height:1.2;}
.aw-cm-sub{font-size:13px;color:var(--dim);margin:3px 0 0;}

/* Search */
.aw-cm-search-wrap{position:relative;margin-bottom:16px;}
.aw-cm-search-icon{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--dim);pointer-events:none;}
.aw-cm-search{
  width:100%;padding:10px 32px 10px 34px;border-radius:7px;
  border:1px solid var(--border);background:var(--surface);
  color:var(--text);font-size:13.5px;font-family:inherit;outline:none;
  transition:border-color .2s var(--ease);
}
.aw-cm-search:focus{border-color:var(--accent);}
.aw-cm-search-clear{
  position:absolute;right:10px;top:50%;transform:translateY(-50%);
  background:none;border:none;color:var(--dim);font-size:18px;cursor:pointer;line-height:1;
}

.aw-cm-empty{
  padding:60px 24px;text-align:center;color:var(--muted);
  border-radius:10px;background:var(--surface);border:1px solid var(--border-l);
}

/* Card list */
.aw-cm-list{display:grid;gap:6px;}

.aw-cm-card{
  border-radius:8px;background:var(--surface);border:1px solid var(--border-l);
  overflow:hidden;transition:border-color .2s var(--ease);
  animation:aw-in .35s var(--ease) both;
}
.aw-cm-card:hover{border-color:var(--border);}
.aw-cm-card-open{border-color:rgba(139,35,50,.3);}

.aw-cm-card-row{display:flex;align-items:center;gap:12px;padding:12px 14px;cursor:pointer;transition:background .15s var(--ease);}
.aw-cm-card-row:hover{background:rgba(240,234,237,.02);}

.aw-cm-avatar{
  width:34px;height:34px;border-radius:8px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:var(--accent-g);border:1px solid rgba(139,35,50,.2);
  font-size:13px;font-weight:800;color:var(--accent);
}
.aw-cm-card-info{flex:1;min-width:0;}
.aw-cm-card-name{font-size:13.5px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.aw-cm-card-meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:2px;font-size:10.5px;color:var(--dim);}

.aw-cm-tag{
  padding:1px 6px;border-radius:3px;font-size:9.5px;font-weight:600;
  background:rgba(107,101,119,.08);color:var(--muted);border:1px solid var(--border-l);
}
.aw-cm-tag-role{background:rgba(74,142,245,.06);color:#7db5f0;border-color:rgba(74,142,245,.12);}

.aw-cm-contact-count{
  display:flex;align-items:center;gap:3px;font-size:11.5px;font-weight:700;
  color:var(--dim);padding:3px 8px;border-radius:5px;
  background:rgba(107,101,119,.06);flex-shrink:0;
}
.aw-cm-cc-has{color:#50d87a;background:rgba(45,212,104,.06);}

.aw-cm-chevron{flex-shrink:0;transition:transform .2s var(--ease);}
.aw-cm-del{flex-shrink:0;opacity:0;transition:opacity .15s var(--ease);}
.aw-cm-card-row:hover .aw-cm-del{opacity:1;}

/* Contacts panel */
.aw-cm-contacts{
  border-top:1px solid var(--border-l);padding:10px 14px;
  background:rgba(27,25,30,.3);
}
.aw-cm-contact{
  display:flex;align-items:center;gap:10px;padding:8px 10px;margin-bottom:4px;
  border-radius:7px;background:var(--surface);border:1px solid var(--border-l);
  transition:border-color .15s var(--ease);
}
.aw-cm-contact:hover{border-color:var(--border);}
.aw-cm-contact-avatar{
  width:28px;height:28px;border-radius:6px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:rgba(74,142,245,.06);border:1px solid rgba(74,142,245,.12);
  font-size:11px;font-weight:800;color:#7db5f0;
}
.aw-cm-contact-info{flex:1;min-width:0;}
.aw-cm-contact-name{font-size:12.5px;font-weight:700;}
.aw-cm-contact-detail{display:flex;gap:4px;flex-wrap:wrap;margin-top:2px;}
.aw-cm-contact-right{font-size:10.5px;color:var(--muted);text-align:right;min-width:100px;flex-shrink:0;}
.aw-cm-contact-line{display:flex;align-items:center;gap:4px;justify-content:flex-end;}
.aw-cm-no-contacts{text-align:center;padding:16px;font-size:12.5px;color:var(--dim);}

.aw-cm-import-info{
  padding:10px 12px;border-radius:7px;
  background:var(--surface);border:1px solid var(--border-l);
}
`;
